import { Group } from 'fabric';
import { assembleSpecs } from '../assemble';
import { applyFill } from '../fill';
import { bubble } from '../kits/calloutKit';
import type { BubbleOptions, BubbleTailSide } from '../kits/calloutKit';
import type { TextMeasurer, TextSpec } from '../kits/specs';
import { estimateMeasure } from '../kits/specs';
import { registerRecipe } from '../registry';
import { tagSmartObject } from '../types';
import type { ContentSourceSpec, FillValue, StandardContentPayload, ToolRecipe } from '../types';

/**
 * Speech / thought bubble (CATALOG, calloutKit.bubble) with a centered,
 * editable caption. THE AUTO-SIZE LAW: the body is measured to the text — the
 * caption is word-wrapped to a balanced box, and the bubble grows to fit it,
 * so retyping a longer line on canvas re-fits the whole bubble on regenerate
 * (no clipping, no manual resizing).
 *
 * The caption carries data.tableCell = {kind:'param', key:'text'} (assemble.ts),
 * so double-clicking it edits in place → params.text → regenerate (one history
 * entry, transform preserved, bubble re-fits).
 *
 * Emits Path (bubble body / thought trail) + FabricText only, grouped and
 * tagged {tool,version,params} — plain fabric JSON, vector at any export dpi.
 */

// ---------------------------------------------------------------------------
// Defensive param coercion (the handwriting.ts pattern).
// ---------------------------------------------------------------------------

function num(v: unknown, fallback: number): number {
  const n = typeof v === 'string' && v.trim() !== '' ? Number(v) : (v as number);
  return typeof n === 'number' && Number.isFinite(n) ? n : fallback;
}

function str(v: unknown, fallback: string): string {
  return typeof v === 'string' && v.trim() !== '' ? v : fallback;
}

/** Unlike str(), an empty caption is MEANINGFUL (= an empty bubble). */
function rawStr(v: unknown, fallback: string): string {
  return typeof v === 'string' ? v : fallback;
}

const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));

// ---------------------------------------------------------------------------
// Text measurement (fraction.ts helper) — real canvas metrics in the browser
// so the bubble fits the picked face; estimate fallback for node/SSR.
// ---------------------------------------------------------------------------

function makeMeasurer(family: string): TextMeasurer {
  try {
    const ctx = document.createElement('canvas').getContext('2d');
    if (!ctx) return estimateMeasure;
    return (text, fontSize) => {
      ctx.font = `${fontSize}px "${family}", sans-serif`;
      const w = ctx.measureText(text).width;
      return Number.isFinite(w) && w >= 0 ? w : estimateMeasure(text, fontSize);
    };
  } catch {
    return estimateMeasure;
  }
}

const INK = '#1e293b';
const DEFAULT_FONT = 'Arial';
const DEFAULT_FILL = '#ffffff';
const LINE_HEIGHT = 1.2;
/** Fabric's single-line box is ~1.13em tall; this is the per-line vertical run. */
const LINE_PITCH = 1.13 * LINE_HEIGHT;

/**
 * Greedy word-wrap to a target pixel width (never splits a word). Whitespace
 * — including any newlines a prior on-canvas edit left in the text — collapses
 * to single break points, so the re-flow is stable across edits.
 */
function wrapLines(text: string, fontSize: number, targetWidth: number, measure: TextMeasurer): string[] {
  const words = text.split(/\s+/).filter(Boolean);
  if (words.length === 0) return [''];
  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    const trial = current ? `${current} ${word}` : word;
    if (current && measure(trial, fontSize) > targetWidth) {
      lines.push(current);
      current = word;
    } else {
      current = trial;
    }
  }
  if (current) lines.push(current);
  return lines;
}

// ---------------------------------------------------------------------------
// Smart-fill seam — say a standard's concept in the bubble.
// ---------------------------------------------------------------------------

/** A speech bubble is a short line, not a card — clip to a sensible length. */
const MAX_BUBBLE_TEXT_LEN = 140;

/** Trim to maxLen without cutting mid-word; append "..." only when cut. */
function truncateForBubble(text: string, maxLen: number): string {
  const t = text.replace(/\s+/g, ' ').trim();
  if (t.length <= maxLen) return t;
  const cut = t.slice(0, maxLen);
  const lastSpace = cut.lastIndexOf(' ');
  const clipped = lastSpace > maxLen * 0.6 ? cut.slice(0, lastSpace) : cut;
  return `${clipped.trim()}...`;
}

const contentSource: ContentSourceSpec = {
  kind: 'standard',
  wants: ['concepts'],
  applyContent(payload: StandardContentPayload, current: Record<string, unknown>) {
    // Merge into a COPY of current so bubble kind/tail/color/font/size are
    // never touched — only `text` (the one content param this recipe has).
    const next = { ...current };
    const concept = Array.isArray(payload?.concepts) ? payload.concepts[0] : undefined;
    if (concept) {
      const raw = (concept.definition || concept.name || '').trim();
      // A concept with neither field is a malformed graph row — leave text
      // unchanged rather than blank the bubble.
      if (raw) next.text = truncateForBubble(raw, MAX_BUBBLE_TEXT_LEN);
    }
    // No title/heading field on this recipe to gate (`text` IS the content).
    // Provenance (survives regenerate — declared schema fields below).
    if (payload?.standard?.code) next.sourceStandardCode = payload.standard.code;
    if (payload?.standard?.id) next.sourceStandardId = payload.standard.id;
    return next;
  },
};

const speechBubble: ToolRecipe = {
  // id is 'callout-bubble', NOT 'speech-bubble': the Decor category already
  // ships a decorative recipe under 'speech-bubble' (recipes/decor.ts). This
  // is the callout sibling — auto-fit to text, speech/thought, dblclick-edit —
  // so it takes a distinct id (ids are the template compatibility contract and
  // must be unique). The teacher-facing name stays "Speech bubble"; the two
  // live in different categories (Decor vs Labels & callouts).
  id: 'callout-bubble',
  domain: 'ela',
  tags: ['speech-bubble', 'dialogue', 'comic', 'story', 'callout', 'thought-bubble'],
  name: 'Speech bubble',
  category: 'callouts',
  version: 1,
  description:
    'A speech or thought bubble that grows to fit its words. Double-click the text to change it — the bubble re-fits automatically.',
  defaults: {
    text: 'Hello!',
    kind: 'speech',
    tailSide: 'bottom',
    tailAt: 0.3,
    color: INK,
    fill: DEFAULT_FILL,
    fontFamily: DEFAULT_FONT,
    fontSize: 44,
    sourceStandardCode: '',
    sourceStandardId: '',
  },
  schema: [
    { key: 'text', label: 'What it says', type: 'text' },
    {
      key: 'kind',
      label: 'Bubble type',
      type: 'select',
      options: [
        { value: 'speech', label: 'Speech' },
        { value: 'thought', label: 'Thought' },
      ],
    },
    {
      key: 'tailSide',
      label: 'Tail side',
      type: 'select',
      options: [
        { value: 'bottom', label: 'Bottom' },
        { value: 'top', label: 'Top' },
        { value: 'left', label: 'Left' },
        { value: 'right', label: 'Right' },
      ],
      section: 'Style',
    },
    {
      key: 'tailAt',
      label: 'Tail position',
      type: 'number',
      min: 0.05,
      max: 0.95,
      step: 0.05,
      help: '0 = start of the side, 1 = end of the side.',
      section: 'Style',
    },
    { key: 'color', label: 'Line & text color', type: 'color', section: 'Style' },
    { key: 'fill', label: 'Bubble fill', type: 'fill', section: 'Style', help: 'Solid, gradient, or texture.' },
    { key: 'fontFamily', label: 'Font', type: 'font', section: 'Style' },
    {
      key: 'fontSize',
      label: 'Text size',
      type: 'number',
      min: 8,
      max: 200,
      step: 1,
      section: 'Style',
    },
    {
      key: 'sourceStandardCode',
      label: 'Source standard code',
      type: 'text',
      advanced: true,
      help: 'Set automatically when filled from a standard.',
    },
    {
      key: 'sourceStandardId',
      label: 'Source standard id',
      type: 'text',
      advanced: true,
      help: 'Set automatically when filled from a standard.',
    },
  ],
  // Smart Tools modal exemplar (SM-A, 2026-07-23): the words + speech/thought
  // choice ARE the callout — hero them; the tail/color/type knobs stay one
  // Style card.
  modal: { hero: ['text', 'kind'] },
  // Honest band values: display text steps down with reading maturity (the
  // bubble auto-fits, so fontSize is the one true size knob); faces walk the
  // house ladder Fredoka → Nunito → Lexend → Arial.
  gradeBands: {
    K2: { fontSize: 64, fontFamily: 'Fredoka' },
    G35: { fontSize: 52, fontFamily: 'Nunito' },
    G68: { fontSize: 44, fontFamily: 'Lexend' },
    G912: { fontSize: 34, fontFamily: 'Arial' },
  },
  contentSource,
  async generate(params) {
    const text = rawStr(params.text, 'Hello!');
    const kind: BubbleOptions['kind'] = params.kind === 'thought' ? 'thought' : 'speech';
    const validSides: BubbleTailSide[] = ['bottom', 'top', 'left', 'right'];
    const tailSide: BubbleTailSide = validSides.includes(params.tailSide as BubbleTailSide)
      ? (params.tailSide as BubbleTailSide)
      : 'bottom';
    const tailAt = clamp(num(params.tailAt, 0.3), 0.05, 0.95);
    const color = str(params.color, INK);
    // Bubble fill upgraded to FillValue: a solid string keeps the str()
    // coercion (back-compat), an object becomes a gradient/texture. `color`
    // (the line & text color) stays a plain color — it feeds the bubble stroke.
    const fillValue: FillValue =
      typeof params.fill === 'string' || params.fill == null
        ? str(params.fill, DEFAULT_FILL)
        : (params.fill as FillValue);
    const family = str(params.fontFamily, DEFAULT_FONT);
    const fontSize = clamp(num(params.fontSize, 44), 6, 400);

    const measure = makeMeasurer(family);
    const linePitch = fontSize * LINE_PITCH;

    // Balance the box: aim for a ~2.4:1 line-length : total-height ratio, but
    // never wrap narrower than the longest single word.
    const singleLine = measure(text.replace(/\s+/g, ' ').trim() || ' ', fontSize);
    const longestWord = text
      .split(/\s+/)
      .filter(Boolean)
      .reduce((w, word) => Math.max(w, measure(word, fontSize)), 0);
    const targetWidth = clamp(
      Math.max(longestWord, Math.sqrt(singleLine * linePitch * 2.4)),
      fontSize * 3.5,
      fontSize * 22,
    );
    const lines = wrapLines(text, fontSize, targetWidth, measure);
    const widestLine = lines.reduce((w, line) => Math.max(w, measure(line || ' ', fontSize)), 0);

    const padX = fontSize * 0.7;
    const padY = fontSize * 0.55;
    const bodyW = widestLine + padX * 2;
    const bodyH = lines.length * linePitch + padY * 2;
    const strokeWidth = clamp(fontSize * 0.09, 2, 24);

    const block = bubble({
      width: bodyW,
      height: bodyH,
      kind,
      tailSide,
      tailAt,
      // Placeholder solid: the pure kit types fill as a string, so a rich
      // FillValue cannot flow through it — the real bubble fill is applied to
      // the assembled body/trail objects below.
      fill: DEFAULT_FILL,
      stroke: color,
      strokeWidth,
    });

    // Caption centered on the BODY (0,0)-(bodyW,bodyH); the tail extends beyond.
    const caption: TextSpec = {
      kind: 'text',
      text: lines.join('\n'),
      x: bodyW / 2,
      y: bodyH / 2,
      fontSize,
      fontFamily: family,
      fill: color,
      originX: 'center',
      originY: 'center',
      param: 'text',
    };

    // The bubble body (speech) — and the shrinking trail circles (thought) —
    // all carry the bubble fill; resolve it once per assembled object. A solid
    // string resolves to itself, so pre-fill designs render byte-identically.
    // The caption keeps its own `color` fill (assembled separately, untouched).
    const bubbleObjs = assembleSpecs(block.specs);
    await Promise.all(bubbleObjs.map((o) => applyFill(o, fillValue, DEFAULT_FILL)));

    const group = new Group([...bubbleObjs, ...assembleSpecs([caption])]);
    group.set({ left: 0, top: 0 });
    return tagSmartObject(group, speechBubble, params);
  },
};

registerRecipe(speechBubble);
